import React, { useEffect, useMemo, useRef } from 'react';
import { ArrowRightOutlined, CheckOutlined, DownOutlined } from '@ant-design/icons';
import { Dropdown } from 'antd';
import styled from 'styled-components';

import ActionRequestButton from 'containers/Overwatch/actions/ActionRequestButton';
import { BOOK_A_DEMO } from 'containers/Overwatch/actions/overwatchActions';
import { useOverwatchActionRequest } from 'containers/Overwatch/actions/useOverwatchAction';
import CardRow from 'containers/Overwatch/components/CardRow';
import {
  ALL_CATEGORIES,
  checkboxCardProps,
  useCategoryFilter,
  useVisibleSelection
} from 'containers/Overwatch/components/useCategorySelection';
import useOverwatchAccess from 'containers/Overwatch/permissions/useOverwatchAccess';

import { ORBIT_PAGE_MOCK } from '../orbitPageMock';
import { IOrbitUnsubscribedFeature, OrbitUnsubscribedState } from '../useOrbitUnsubscribedFeatures';
import { pg, PlainButton, tint, type } from './pageStyles';

const Root = styled.div`
  > * + * {
    margin-top: 12px;
  }
`;

const Banner = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  min-height: 53px;
  padding: 8px 20px;
  border-radius: 12px;
  background: ${pg.card};
`;

const BannerText = styled.div`
  min-width: 0;
  margin-right: 16px;
`;

const BannerTitle = styled.h2`
  margin: 0;
  color: ${pg.text};
  ${type('containerTitle', 700)}
`;

const BannerSubtitle = styled.p`
  margin: 0;
  color: ${pg.muted};
  ${type('helping')}
`;

const BannerActions = styled.div`
  display: flex;
  align-items: center;

  > * + * {
    margin-left: 10px;
  }
`;

const FilterPill = styled(PlainButton)`
  display: inline-flex;
  align-items: center;
  height: 25px;
  padding: 0 12px;
  border: 1px solid ${tint('#FFFFFF', 0.5)};
  border-radius: 36px;
  color: ${pg.text};
  ${type('helping')}

  > * + * {
    margin-left: 8px;
  }

  &:disabled {
    cursor: default;
  }
`;

const DemoButton = styled(PlainButton)`
  display: inline-flex;
  align-items: center;
  height: 24px;
  padding: 0 14px;
  border-radius: 7px;
  background: ${pg.violet};
  color: #ffffff;
  ${type('helping', 700)}

  > * + * {
    margin-left: 6px;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SelectRow = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const SelectAll = styled(PlainButton)`
  height: 25px;
  padding: 0 10px;
  border-radius: 6px;
  background: #808080;
  color: #ffffff;
  ${type('helping', 600)}
`;

const Card = styled.article<{ $selected: boolean }>`
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 199px;
  padding: 17px 19px;
  border: 1px solid ${({ $selected }) => ($selected ? pg.violet : 'transparent')};
  border-radius: 12px;
  background: ${pg.card};
  cursor: pointer;
  outline: none;

  &:hover {
    border-color: ${({ $selected }) => ($selected ? pg.violet : pg.iconTileStroke)};
  }

  &:focus-visible {
    border-color: ${pg.link};
  }
`;

const CardHead = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 12px;
`;

const Checkbox = styled.span<{ $checked: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border: 1px solid ${({ $checked }) => ($checked ? pg.violet : pg.iconTileStroke)};
  border-radius: 8px;
  background: ${({ $checked }) => ($checked ? pg.violet : 'transparent')};
  color: #ffffff;
`;

const NotSubscribed = styled.span`
  padding: 2px 10px;
  border: 1px solid ${pg.border};
  border-radius: 20px;
  background: ${pg.rail};
  color: ${pg.muted};
  white-space: nowrap;
  ${type('helping', 600)}
`;

const CardTitle = styled.h3`
  margin: 0;
  color: ${pg.text};
  ${type('conflictBody', 700)}
`;

const CardCategory = styled.p`
  margin: 4px 0 0;
  color: ${pg.muted};
  ${type('helping')}
`;

const CardDescription = styled.p`
  margin: 8px 0 0;
  color: ${pg.muted};
  ${type('helping')}
`;

const Note = styled.p`
  margin: 0;
  padding: 24px 20px;
  border-radius: 12px;
  background: ${pg.card};
  color: ${pg.muted};
  ${type('containerBody')}
`;

interface ICapabilityCardProps {
  feature: IOrbitUnsubscribedFeature;
  selected: boolean;
  onToggle: (id: string) => void;
  cardRef: (node: HTMLElement | null) => void;
}

const CapabilityCard: React.FC<ICapabilityCardProps> = ({
  feature,
  selected,
  onToggle,
  cardRef
}) => (
  <Card
    ref={cardRef}
    data-testid="orbit-page-capability"
    {...checkboxCardProps(selected, feature.title, () => onToggle(feature.id))}
    $selected={selected}
  >
    <CardHead>
      <Checkbox $checked={selected} aria-hidden="true">
        {selected ? <CheckOutlined /> : null}
      </Checkbox>
      <NotSubscribed>{feature.statusLabel}</NotSubscribed>
    </CardHead>
    <CardTitle>{feature.title}</CardTitle>
    {feature.category ? <CardCategory>{feature.category.label}</CardCategory> : null}
    {feature.description ? <CardDescription>{feature.description}</CardDescription> : null}
  </Card>
);

interface IOrbitExploreMoreProps {
  state: OrbitUnsubscribedState;
  focusFeatureId?: string | null;
}

const categoryOf = (feature: IOrbitUnsubscribedFeature) => feature.category;
const idOf = (feature: IOrbitUnsubscribedFeature) => feature.id;

const OrbitExploreMore: React.FC<IOrbitExploreMoreProps> = ({ state, focusFeatureId = null }) => {
  const { explore } = ORBIT_PAGE_MOCK;
  const { canBookDemo } = useOverwatchAccess();
  const demo = useOverwatchActionRequest(BOOK_A_DEMO, canBookDemo);
  const features = useMemo(() => (state.status === 'ready' ? state.features : []), [state]);
  const { category, setCategory, options, visible } = useCategoryFilter(features, categoryOf);
  const { isSelected, toggle, add, allVisibleSelected, toggleAll } = useVisibleSelection(
    visible,
    idOf,
    focusFeatureId ? [focusFeatureId] : []
  );
  const cards = useRef(new Map<string, HTMLElement>());

  useEffect(() => {
    if (!focusFeatureId) return;
    setCategory(ALL_CATEGORIES);
    add(focusFeatureId);
  }, [focusFeatureId, setCategory, add]);

  const focusReady =
    focusFeatureId !== null && features.some(feature => feature.id === focusFeatureId);

  useEffect(() => {
    if (focusReady && focusFeatureId) {
      cards.current.get(focusFeatureId)?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
    }
  }, [focusReady, focusFeatureId]);

  const renderBody = (): React.ReactNode => {
    if (state.status === 'loading') return null;
    if (state.status === 'hidden') {
      return <Note role="status">Feature discovery is not available right now.</Note>;
    }
    if (visible.length === 0) {
      return <Note role="status">No additional features to explore.</Note>;
    }
    return (
      <>
        <SelectRow>
          <SelectAll type="button" aria-pressed={allVisibleSelected} onClick={toggleAll}>
            Select All
          </SelectAll>
        </SelectRow>
        <CardRow $columns={4} $min={260} $balance={false}>
          {visible.map(feature => (
            <CapabilityCard
              key={feature.id}
              feature={feature}
              selected={isSelected(feature.id)}
              onToggle={toggle}
              cardRef={node => {
                if (node) {
                  cards.current.set(feature.id, node);
                } else {
                  cards.current.delete(feature.id);
                }
              }}
            />
          ))}
        </CardRow>
      </>
    );
  };

  const menuItems = [{ key: ALL_CATEGORIES, label: explore.filter }, ...options];
  const categoryLabel = options.find(option => option.key === category)?.label ?? explore.filter;

  return (
    <Root data-testid="orbit-page-explore">
      <Banner>
        <BannerText>
          <BannerTitle>{explore.title}</BannerTitle>
          <BannerSubtitle>{explore.subtitle}</BannerSubtitle>
        </BannerText>
        <BannerActions>
          <Dropdown
            trigger={['click']}
            disabled={state.status !== 'ready'}
            menu={{
              items: menuItems,
              selectable: true,
              selectedKeys: [category],
              onClick: ({ key }) => setCategory(key)
            }}
          >
            <FilterPill type="button" aria-label="Category" aria-haspopup="menu">
              <span>{categoryLabel}</span>
              <DownOutlined aria-hidden="true" />
            </FilterPill>
          </Dropdown>
          <ActionRequestButton request={demo} button={DemoButton}>
            <span>Book a Demo</span>
            <ArrowRightOutlined aria-hidden="true" />
          </ActionRequestButton>
        </BannerActions>
      </Banner>
      {renderBody()}
    </Root>
  );
};

export default OrbitExploreMore;
